import {useCallback, useRef} from 'react';
import {useLatest} from 'ahooks';
import type {AsyncVoidFunction} from '@Utils/general.util';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {useAiPromptBoxPendingUploads} from '@Components/ai-prompt-box/hooks/useAiPromptBoxPendingUploads';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';

export interface UseAiPromptBoxSubmitFlow {
  submit: AsyncVoidFunction;
  cancelSoftSend: VoidFunction;
}

export const useAiPromptBoxSubmitFlow = (id: string, onGenerate: AsyncVoidFunction): UseAiPromptBoxSubmitFlow => {
  const {state, actions} = useAiPromptBox(id);
  const stateRef = useLatest(state);
  const onGenerateRef = useLatest(onGenerate);
  const {hasInFlightUploads, awaitAll, getCurrentPendingSnapshot} = useAiPromptBoxPendingUploads(id);
  const hasInFlightUploadsRef = useLatest(hasInFlightUploads);
  const softSendAbortRef = useRef<AbortController | null>(null);
  const isSubmittingRef = useRef(false);

  const submit = useCallback(async (): Promise<void> => {
    if (isSubmittingRef.current) {
      return;
    }
    const currentState = stateRef.current;
    if (currentState.isRecordingAudio || currentState.isProcessingAudio) {
      return;
    }
    if (!isPromptValid(currentState.prompt)) {
      return;
    }

    isSubmittingRef.current = true;
    try {
      if (hasInFlightUploadsRef.current) {
        const abortController = new AbortController();
        softSendAbortRef.current = abortController;
        actions.update({isFinishingUploads: true});

        try {
          const result = await raceWithAbortSignal(awaitAll(), abortController.signal);
          if (result.aborted) {
            return;
          }
        } finally {
          if (softSendAbortRef.current === abortController) {
            softSendAbortRef.current = null;
          }
          actions.update({isFinishingUploads: false});
        }
      }

      if (getCurrentPendingSnapshot().length > 0) {
        showFailedUploadsGrowl();
        return;
      }
    } finally {
      isSubmittingRef.current = false;
    }

    await onGenerateRef.current();
  }, [actions, stateRef, hasInFlightUploadsRef, awaitAll, getCurrentPendingSnapshot, onGenerateRef]);

  const cancelSoftSend = useCallback((): void => {
    softSendAbortRef.current?.abort();
  }, []);

  return {submit, cancelSoftSend};
};

const isPromptValid = (prompt: string | undefined): boolean => {
  return !!prompt && prompt.trim().length > 0;
};

const showFailedUploadsGrowl = (): void => {
  showMessageGrowl({
    type: GROWL_TYPE.DANGER,
    text: window.i18next.t('pmwjs_ai_chat_remove_failed_uploads_to_continue'),
    showCloseIcon: true,
    interval: 7000,
  });
};

type RaceOutcome<T> = {aborted: false; value: T} | {aborted: true};

const raceWithAbortSignal = async <T>(promise: Promise<T>, signal: AbortSignal): Promise<RaceOutcome<T>> => {
  if (signal.aborted) {
    return {aborted: true};
  }
  const abortPromise = new Promise<RaceOutcome<T>>((resolve) => {
    signal.addEventListener('abort', (): void => {
      resolve({aborted: true});
    }, {once: true});
  });
  const completionPromise = promise.then((value): RaceOutcome<T> => ({aborted: false, value}));
  return Promise.race([completionPromise, abortPromise]);
};
